import { format, parse, isValid } from "date-fns";

/**
 * Shop's pre-submit Review card.
 * Renders the same pricing layout the customer will see, minus customer-only
 * UI (no BEST PRICE auto-tag, no Accept Quote button, no shop strip,
 * no View Shop Profile link). Used in the Review step of the multi-quote dialog.
 *
 * Inputs are the *form values* (raw strings from the multi-quote form),
 * not a saved quote document — math mirrors backend GST treatment in
 * CustomerQuotesDialog::computePricing.
 */
export function ShopReviewCard({ quote, optionNumber, pricesIncludeGst, qty, isTyreService, tyreSize }) {
  const tyreFitment = parseFloat(quote.tyre_fitment_cost) || 0;
  const alignmentRaw = parseFloat(quote.alignment_cost) || 0;
  const rotationRaw = parseFloat(quote.rotation_cost) || 0;

  // Convert form prices → incl. GST for display (the customer always sees incl. GST)
  const factor = pricesIncludeGst ? 1 : 1.15;
  const tyreFitInclGst = tyreFitment * factor;
  const alignmentInclGst = alignmentRaw * factor;
  const rotationInclGst = rotationRaw * factor;
  const totalInclGst = tyreFitInclGst + alignmentInclGst + rotationInclGst;

  const isTyre = isTyreService && tyreFitInclGst > 0 && qty > 0;
  const perInclGst = isTyre ? tyreFitInclGst / qty : 0;
  const perExclGst = perInclGst / 1.15;

  const stockLabel = (quote.stock_availability || "in_stock").replace(/_/g, " ");
  // DatePicker emits "DD/MM/YYYY" strings (NZ format); also tolerate Dates
  // and ISO strings. `new Date("15/05/2026")` is Invalid in JS, hence the
  // explicit DD/MM/YYYY parse first.
  const safeFormat = (v) => {
    if (!v) return null;
    if (v instanceof Date) {
      return isValid(v) ? format(v, "d MMM yyyy") : null;
    }
    if (typeof v === "string") {
      // Try DD/MM/YYYY first
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
        const d = parse(v, "dd/MM/yyyy", new Date());
        return isValid(d) ? format(d, "d MMM yyyy") : null;
      }
      // Fallback: ISO / RFC strings
      const d = new Date(v);
      return isValid(d) ? format(d, "d MMM yyyy") : null;
    }
    return null;
  };
  const fitmentDate = safeFormat(quote.service_date_estimate);
  const expiryDate = safeFormat(quote.quote_expiry);

  return (
    <div
      className="shrink-0 w-[290px] rounded-xl border-2 border-input bg-card p-4 space-y-3"
      data-testid={`review-card-${optionNumber}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold grid place-items-center">
          {optionNumber}
        </span>
        <p className="font-semibold text-sm">Quote Option {optionNumber}</p>
      </div>

      {/* Pricing hero — mirrors customer view */}
      <div className="border-t pt-3">
        {isTyre ? (
          <>
            <p
              className="text-3xl font-heading font-bold text-primary leading-none"
              data-testid={`review-per-tyre-excl-gst-${optionNumber}`}
            >
              ${perExclGst.toFixed(2)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">
              excl. GST per tyre fitted
            </p>
            <p className="text-base font-semibold text-foreground/80 mt-1.5 leading-none">
              ${perInclGst.toFixed(2)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              incl. GST per tyre fitted
            </p>
          </>
        ) : (
          <>
            <p className="text-3xl font-heading font-bold text-primary leading-none">
              ${totalInclGst.toFixed(2)}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">incl. GST total</p>
          </>
        )}
      </div>

      {/* Tyre details */}
      {isTyreService && (quote.tyre_brand || quote.tyre_model) && (
        <div className="text-xs space-y-0.5 border-t pt-2.5">
          <p className="font-medium text-foreground">
            {quote.tyre_brand} {quote.tyre_model}
          </p>
          {(tyreSize || quote.speed_rating) && (
            <p className="text-muted-foreground">
              {tyreSize}
              {tyreSize && quote.speed_rating && " "}
              {quote.speed_rating}
            </p>
          )}
          <p className="text-muted-foreground">{stockLabel}</p>
          {fitmentDate && (
            <p className="text-muted-foreground">Earliest fitment date {fitmentDate}</p>
          )}
        </div>
      )}
      {!isTyreService && fitmentDate && (
        <div className="text-xs border-t pt-2.5">
          <p className="text-muted-foreground">Earliest fitment date {fitmentDate}</p>
        </div>
      )}

      {/* Notes — shop's pitch to the customer */}
      {quote.notes && (
        <div className="text-xs border-t pt-2.5">
          <p className="font-medium text-foreground/80 mb-0.5">Notes to customer</p>
          <p
            className="text-muted-foreground italic whitespace-pre-wrap"
            data-testid={`review-notes-${optionNumber}`}
          >
            “{quote.notes}”
          </p>
        </div>
      )}

      {/* Breakdown — same lines the customer sees */}
      <div className="text-[11px] text-muted-foreground border-t pt-2 space-y-0.5">
        {tyreFitInclGst > 0 && (
          <div className="flex justify-between">
            <span>Total fitment</span>
            <span>${tyreFitInclGst.toFixed(2)}</span>
          </div>
        )}
        {alignmentInclGst > 0 && (
          <div className="flex justify-between">
            <span>Tyre alignment</span>
            <span>${alignmentInclGst.toFixed(2)}</span>
          </div>
        )}
        {rotationInclGst > 0 && (
          <div className="flex justify-between">
            <span>Tyre rotation</span>
            <span>${rotationInclGst.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-medium text-foreground pt-1 border-t mt-1">
          <span>Total payable</span>
          <span data-testid={`review-total-${optionNumber}`}>
            ${totalInclGst.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Validity */}
      {expiryDate && (
        <div className="text-[11px] text-muted-foreground border-t pt-2">
          ⏱ Valid until {expiryDate}
        </div>
      )}
    </div>
  );
}
